import { Link, NavLink, useNavigate } from "react-router-dom";
import { Globe2, LogOut, Sparkles, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      data-testid="app-navbar"
      className="sticky top-0 z-40 w-full border-b border-slate-200/70 dark:border-slate-800/60 backdrop-blur-xl bg-white/80 dark:bg-slate-950/80"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-2">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 shadow-lg shadow-teal-500/25">
            <Globe2 className="h-5 w-5 text-white" strokeWidth={2.4} />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">VisAura</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {[
            { to: "/checker", label: "Checker" },
            { to: "/pricing", label: "Pricing" },
            user && { to: "/dashboard", label: "Dashboard" },
          ]
            .filter(Boolean)
            .map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="nav-user-menu"
                  className="gap-2"
                >
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white dark:bg-white dark:text-slate-900">
                    {user.full_name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden text-sm font-medium sm:inline">
                    {user.full_name?.split(" ")[0]}
                  </span>
                  {user.is_premium && (
                    <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">{user.full_name}</span>
                    <span className="text-xs text-slate-500">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/dashboard")}
                  data-testid="menu-dashboard"
                >
                  <User className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                {!user.is_premium && (
                  <DropdownMenuItem
                    onClick={() => navigate("/pricing")}
                    data-testid="menu-upgrade"
                  >
                    <Sparkles className="mr-2 h-4 w-4 text-amber-500" /> Upgrade to Premium
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  data-testid="menu-logout"
                  className="text-rose-600"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/login")}
                data-testid="nav-login-btn"
              >
                Log in
              </Button>
              <Button
                size="sm"
                onClick={() => navigate("/register")}
                data-testid="nav-register-btn"
                className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
              >
                Get started
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
